import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { useStreamChat } from '../../../context/StreamChatContext';
import { itemMatches } from '../../../config/navConfig';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '../../ui/sidebar';
import OrgSwitcher from './OrgSwitcher';
import NavUser from './NavUser';
import { openAssistant, openCommandPalette, openSupport } from './shellEvents';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

const ACTIONS = {
  assistant: openAssistant,
  support: openSupport,
};

const isItemActive = (item, pathname) =>
  itemMatches(item, pathname) || (item.children || []).some((c) => itemMatches(c, pathname));

const formatBadge = (n) => (n > 99 ? '99+' : String(n));

/** Leaf row: a link, or an action button (assistant / support). */
function LeafItem({ item, pathname, badge }) {
  const { setOpenMobile } = useSidebar();
  const Icon = item.icon;
  const content = (
    <>
      {Icon && <Icon />}
      <span>{item.title}</span>
    </>
  );

  return (
    <SidebarMenuItem data-tour={`nav-${item.key}`}>
      {item.action ? (
        <SidebarMenuButton
          tooltip={item.title}
          onClick={() => {
            setOpenMobile(false);
            ACTIONS[item.action]?.();
          }}
        >
          {content}
        </SidebarMenuButton>
      ) : (
        <SidebarMenuButton asChild tooltip={item.title} isActive={itemMatches(item, pathname)}>
          <Link to={item.to} onClick={() => setOpenMobile(false)}>{content}</Link>
        </SidebarMenuButton>
      )}
      {item.badge && (
        <SidebarMenuBadge className="bg-[var(--sidebar-badge-bg)] text-[var(--sidebar-badge-fg)] uppercase tracking-wide">
          {item.badge}
        </SidebarMenuBadge>
      )}
      {badge > 0 && <SidebarMenuBadge>{formatBadge(badge)}</SidebarMenuBadge>}
    </SidebarMenuItem>
  );
}

/**
 * Parent row with sub-items. Expanded sidebar: collapsible list (auto-opens
 * when a child route is active). Icon-only sidebar: a flyout menu to the
 * right, so sub-pages stay one click away (a hover peek shows the list).
 */
function ParentItem({ item, pathname }) {
  const { state, peek, isMobile, setOpenMobile } = useSidebar();
  const active = isItemActive(item, pathname);
  const [open, setOpen] = useState(active);
  const Icon = item.icon;

  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  // A hover peek shows the full tree, so only the pinned-collapsed rail uses the flyout.
  if (state === 'collapsed' && !peek && !isMobile) {
    return (
      <SidebarMenuItem data-tour={`nav-${item.key}`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton isActive={active} aria-label={item.title}>
              {Icon && <Icon />}
              <span>{item.title}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" sideOffset={10} className="min-w-52">
            <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {item.children.map((child) => (
              <DropdownMenuItem
                key={child.key}
                asChild
                className={itemMatches(child, pathname) ? 'bg-[var(--lodge-onyx-50)] font-medium' : undefined}
              >
                <Link to={child.to}>{child.title}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem data-tour={`nav-${item.key}`}>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={item.title} isActive={active && !open}>
            {Icon && <Icon />}
            <span>{item.title}</span>
            <ChevronRight className="ml-auto !size-3.5 text-sidebar-muted transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <SidebarMenuSub>
            {item.children.map((child) => {
              const childActive = itemMatches(child, pathname);
              return (
                <SidebarMenuSubItem key={child.key} className="relative">
                  {childActive && (
                    <span aria-hidden className="absolute -left-[11px] top-1 bottom-1 w-0.5 rounded-full bg-sidebar-primary" />
                  )}
                  <SidebarMenuSubButton asChild isActive={childActive}>
                    <Link to={child.to} onClick={() => setOpenMobile(false)}>
                      <span>{child.title}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function NavItems({ items, pathname, badges = {} }) {
  return (
    <SidebarMenu>
      {items.filter((item) => item.sidebar !== false).map((item) =>
        item.children && item.children.length > 0 ? (
          <ParentItem key={item.key} item={item} pathname={pathname} />
        ) : (
          <LeafItem key={item.key} item={item} pathname={pathname} badge={badges[item.badgeSource]} />
        )
      )}
    </SidebarMenu>
  );
}

function SearchButton() {
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenu>
      <SidebarMenuItem data-tour="search">
        <SidebarMenuButton
          tooltip="Search"
          onClick={() => {
            if (isMobile) setOpenMobile(false);
            openCommandPalette();
          }}
          className="bg-[var(--sidebar-surface)] text-sidebar-muted shadow-[inset_0_0_0_1px_var(--sidebar-border)] hover:text-[var(--sidebar-strong)]"
        >
          <Search />
          <span>Search</span>
          {!isMobile && (
            <kbd className="ml-auto rounded border border-sidebar-border px-1.5 py-px font-mono text-[10px] text-sidebar-muted group-data-[collapsible=icon]:hidden">
              {isMac ? '⌘K' : 'Ctrl K'}
            </kbd>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** Desktop app sidebar — inset variant, collapsible to icons; colours come from the sidebar theme tokens (appShell.css). */
// Phone New look: the sheet opens under the app bar, which keeps working
// while it is open — its menu button closes the sheet itself, so a press
// there must not also count as an outside click (that would reopen it).
const MOBILE_SHEET_PROPS = {
  onInteractOutside: (e) => {
    const target = e.detail?.originalEvent?.target || e.target;
    if (target?.closest?.('[data-mobile-sheet-toggle]')) e.preventDefault();
  },
};

function AppSidebar({ nav, pathname }) {
  const { unreadMessageCount } = useStreamChat();
  const { isMobile } = useSidebar();
  const badges = { chat: unreadMessageCount || 0 };

  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      mobileClassName="app-mobile-sheet"
      mobileContentProps={MOBILE_SHEET_PROPS}
    >
      <SidebarHeader>
        {/* on phones the org switcher lives in the app bar above the sheet */}
        {!isMobile && <OrgSwitcher />}
        <SearchButton />
      </SidebarHeader>
      <SidebarContent>
        {nav.main.map((group) => (
          <SidebarGroup key={group.key} className="py-1" data-tour={`group-${group.key}`}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <NavItems items={group.items} pathname={pathname} />
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <NavItems items={nav.footer} pathname={pathname} badges={badges} />
        <SidebarSeparator className="mx-0" />
        <NavUser settings={nav.settings} pathname={pathname} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

// The layout re-renders on every navigation (route, loading bar); the
// sidebar only needs to when the nav tree or the active path changes.
export default React.memo(AppSidebar);
